import { isCvUuid, publicCvPath, type PublicCvKind } from '~/utils/publicCvLink'

// The layout and the page both resolve the same param on mount: one lookup serves both.
const lookups = new Map<string, Promise<string | null>>()

/**
 * The cv_requests id behind /cv/public/[id] and /card/public/[id], whose param is
 * either the CV's UUID or its public slug (utils/publicCvLink.ts). Resolves to
 * null when no CV the visitor may see has that slug.
 */
export function usePublicCvId() {
  const route = useRoute()
  const supabase = useSupabaseClient()

  function resolvePublicCvId(): Promise<string | null> {
    const param = String(route.params.id ?? '')
    if (!param) return Promise.resolve(null)
    if (isCvUuid(param)) return Promise.resolve(param)

    let lookup = lookups.get(param)
    if (!lookup) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      lookup = (supabase as any)
        .from('cv_requests')
        .select('id')
        .eq('public_url_slug', param)
        .is('deleted_at', null)
        .maybeSingle()
        .then(({ data }: { data: { id: string } | null }) => data?.id ?? null)
        .catch(() => null) as Promise<string | null>
      lookups.set(param, lookup)
      // A miss is not cached for good: the owner may be about to publish it.
      lookup.then((id) => { if (!id) lookups.delete(param) })
    }
    return lookup
  }

  return { resolvePublicCvId }
}

/**
 * Public links for the signed-in owner's own CVs. The editor, the CV list and the
 * share dialogs only know a CV's id; whoever loads the row remembers its slug
 * here, so every link shown is the readable one as soon as there is one.
 */
export function usePublicCvLinks() {
  const slugs = useState<Record<string, string>>('cv-public-slugs', () => ({}))

  function rememberPublicSlug(cv: { id: string, public_url_slug?: string | null }) {
    if (cv.id && cv.public_url_slug && slugs.value[cv.id] !== cv.public_url_slug) {
      slugs.value = { ...slugs.value, [cv.id]: cv.public_url_slug }
    }
  }

  function publicCvLinkPath(kind: PublicCvKind, cvId: string): string {
    return publicCvPath(kind, { id: cvId, public_url_slug: slugs.value[cvId] })
  }

  return { rememberPublicSlug, publicCvLinkPath }
}
